'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { LinkButton } from '@/components/ui/button'
import Section from '@/components/marketing/Section'
import MonitoringStackLogos from '@/components/MonitoringStackLogos'
import EngineLogos from '@/components/home/EngineLogos'

/**
 * The category statement AND the ask: a full-width editorial pause after
 * the category argument (VisibilityGap). Names the category Radar is built
 * for — decision-stage AI visibility — carries the homepage conversion CTA
 * (moved here from the retired ConversionBand card, same data-cta-id so
 * funnel telemetry stays continuous), and closes with the monitoring-stack
 * complement framing that previously ended VisibilityGap, so the
 * monitoring-vs-decision-layer contrast lives in exactly one place.
 * Present-tense claims stay on shipped capability (signal audit, fix
 * prompts); the homepage deliberately never presents decision win-rate
 * as a shipped capability — its roadmap framing lives on the
 * methodology page, llms.txt / llms-full.txt, and the knowledge graph.
 *
 * Choreography: same data-phase contract as AnswerReadoutCard — fully
 * visible by default (SSR / no-JS / reduced motion), arms only once JS
 * confirms motion is allowed, then plays a one-time two-stage headline
 * rise followed by the supporting copy when the section enters the
 * viewport. Motion spec lives in globals.css under "Decision-stage
 * editorial reveal".
 */
export default function DecisionStage() {
  const frameRef = useRef<HTMLDivElement>(null)
  // 'idle' = everything visible (SSR / no-JS / reduced motion),
  // 'armed' = hidden awaiting viewport entry, 'run' = one-time sequence.
  const [phase, setPhase] = useState<'idle' | 'armed' | 'run'>('idle')

  useEffect(() => {
    const el = frameRef.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    setPhase('armed')
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPhase('run')
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <Section width='wide'>
      <div ref={frameRef} className='decision-stage' data-phase={phase}>
        {/* The engines where the decision happens — a kicker claim over a
            quiet monochrome row, the deliberate bookend to the section's
            closing "Pairs with your monitoring stack" strip (same
            instrument-label treatment, opposite meaning: engines buyers
            consult vs complementary tools). */}
        <p className='ds-seq instrument-label text-center m-0 mb-6'>
          The AI engines buyers ask before they buy
        </p>
        <EngineLogos className='ds-seq mb-8 md:mb-10' />

        {/* The claim, not the category label — the category term now lives
            in the body's first sentence so the page carries one declaration,
            not two (Lloyd, 2026-07-12). Keeps the full --category scale
            (Lloyd explicit: copy change, never a size reduction); the longer
            string wraps naturally at that ramp. Two inline-block spans keep
            the two-stage reveal; inline spans can't take the rise transform.
            "decision stage." carries the grade-gradient on a nested span so
            the sweep and the rise animate separately. */}
        <h2 className='thesis-headline thesis-headline--category text-center'>
          <span className='ds-seq inline-block'>
            Radar gives you visibility
          </span>{' '}
          <span
            className='ds-seq inline-block'
            style={{ animationDelay: '160ms' }}
          >
            at the <span className='text-cta'>decision stage.</span>
          </span>
        </h2>

        <p
          className='ds-seq text-muted-foreground leading-relaxed text-center m-0 mt-6 md:mt-8 max-w-2xl mx-auto'
          style={{ animationDelay: '360ms' }}
        >
          Radar is built for decision-stage AI visibility. It checks whether AI
          can read your site, samples what AI answers say about you, and hands
          your team a suggested fix for each finding.
        </p>

        {/* The ask — button and reassurance as one centered unit */}
        <div
          className='ds-seq flex flex-col items-center gap-2.5 mt-8 md:mt-10'
          style={{ animationDelay: '600ms' }}
        >
          <LinkButton
            href='/platform#free-audit'
            variant='action'
            size='lg'
            data-cta-id='band'
          >
            Get My Free Snapshot
          </LinkButton>
          <p className='text-xs text-muted-foreground m-0'>
            No credit card. Access in 30 seconds.
          </p>
        </div>

        {/* Companion positioning — different layer, not a competitor.
            Moved here from VisibilityGap so the monitoring contrast is
            stated once, directly under the category definition. */}
        <div
          className='ds-seq mt-14 pt-10 border-t border-line'
          style={{ animationDelay: '720ms' }}
        >
          <p className='instrument-label text-center mb-6'>
            <Link
              href='/blogs/ai-monitoring-vs-ai-technical-readiness'
              data-cta-id='article'
              /* inline-block + vertical padding: at 11px instrument-label type
                 the bare inline box was ~13px tall, under the 24px WCAG 2.2
                 minimum target size. */
              className='inline-block py-2 hover:text-foreground transition-colors'
            >
              Pairs with your monitoring stack
            </Link>
          </p>
          <MonitoringStackLogos />
          <p className='text-sm text-foreground text-center leading-relaxed mt-6 max-w-xl mx-auto'>
            They track <span className='font-semibold'>what</span> AI says about
            you. Radar shows you{' '}
            <span className='font-semibold'>what to change</span>.{' '}
            <span className='text-accent-cyan font-semibold'>Run both.</span>
          </p>
        </div>
      </div>
    </Section>
  )
}
